import { useMemo, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { TooltipProvider } from "@/components/ui/tooltip";
import ProductSuggestionForm from "@/components/ProductSuggestionForm";
import MemberHeaderWallet from "@/components/MemberHeaderWallet";
import SidebarCollapseTab from "@/components/shared/SidebarCollapseTab";
import SidebarNavTooltip from "@/components/shared/SidebarNavTooltip";
import { usePersistedSidebarCollapsed } from "@/hooks/usePersistedSidebarCollapsed";
import { cn } from "@/lib/utils";
import { MEMBER_DASH_TAB_ICONS, MEMBER_GLOBAL_NAV_ICONS } from "@/lib/navIcons";
import BrandLogo from "@/components/BrandLogo";
import { AlignJustify, LayoutGrid, LogOut, Mail } from "lucide-react";

const SIDEBAR_EXPANDED = 288;
const SIDEBAR_COLLAPSED = 72;

const DASH_SIDEBAR_ICONS = MEMBER_DASH_TAB_ICONS;

/**
 * Layout CRM pour les utilisateurs connectés — même logique que la console admin.
 * @param {Array<{ tab: string; label: string; badge?: boolean; testId?: string }>=} dashboardNav Rubriques affichées au-dessus des liens Marketplace (sans « Tableau de bord » global dupliqué).
 */
export default function MemberShell({ title, subtitle, children, dashboardNav = null, onDashboardNavClick = null }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname || "";
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [writeUsOpen, setWriteUsOpen] = useState(false);
  const { collapsed, toggle: toggleCollapsed } = usePersistedSidebarCollapsed();
  const showWriteUs = Boolean(user && user.role !== "admin");

  const dashTabResolved = useMemo(() => {
    if (pathname !== "/dashboard") return null;
    const raw = new URLSearchParams(location.search || "").get("tab") || "";
    if (raw === "overview") return "overview";
    return raw && raw in DASH_SIDEBAR_ICONS ? raw : null;
  }, [pathname, location.search]);

  const dashHref = useCallback((tab) => {
    return `/dashboard?tab=${encodeURIComponent(tab)}`;
  }, []);

  /** Liens hors tableau de bord (Marketplace, compte…) — sans doublon « Tableau de bord » lorsque dashboardNav fourni. */
  const globalNavItems = useMemo(() => {
    const items = [];
    if (!dashboardNav?.length) {
      items.push({
        key: "dashboard",
        to: "/dashboard?tab=overview",
        label: "Tableau de bord",
        Icon: MEMBER_GLOBAL_NAV_ICONS.dashboard,
        active:
          pathname === "/dashboard" &&
          (new URLSearchParams(location.search || "").get("tab") || "") === "overview",
      });
    }
    items.push(
      {
        key: "marketplace",
        to: "/marketplace",
        label: "Marketplace",
        Icon: MEMBER_GLOBAL_NAV_ICONS.marketplace,
        active: pathname === "/marketplace" || pathname === "/catalogue",
      },
      {
        key: "publish",
        to: "/nouveau-dossier",
        label: "Publier une annonce",
        Icon: MEMBER_GLOBAL_NAV_ICONS.publish,
        active: pathname.startsWith("/nouveau-dossier"),
      },
      {
        key: "account",
        to: "/compte",
        label: "Mon compte",
        Icon: MEMBER_GLOBAL_NAV_ICONS.account,
        active: pathname === "/compte",
      }
    );
    return items;
  }, [pathname, location.search, dashboardNav]);

  const SidebarBody = ({ navScrollable = false, sidebarCollapsed = false }) => (
    <div className="flex h-full min-h-0 flex-col bg-[#0f172a] text-white">
      <div className={cn("shrink-0 border-b border-white/10 py-4", sidebarCollapsed ? "px-2" : "px-4")}>
        <div className={cn("flex items-center", sidebarCollapsed ? "justify-center" : "gap-2.5")}>
          <BrandLogo
            to="/"
            size="sm"
            showWordmark={!sidebarCollapsed}
            wordmarkClassName="text-sm text-white"
            linkClassName={cn(sidebarCollapsed ? "justify-center" : "min-w-0 flex-1")}
            onClick={() => setMobileNavOpen(false)}
            data-testid="member-sidebar-logo"
          />
        </div>
      </div>

      <nav
        className={cn(
          "flex-1 min-h-0 space-y-0.5 py-3",
          sidebarCollapsed ? "px-1.5" : "px-2",
          navScrollable ? "overflow-y-auto" : "overflow-hidden",
        )}
        aria-label="Navigation membre"
      >
        {dashboardNav?.length ? (
          <div className="mb-1">
            {!sidebarCollapsed ? (
              <p className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Activité marché
              </p>
            ) : null}
            <div className="space-y-0.5">
              {dashboardNav.map((row) => {
                const Icon = DASH_SIDEBAR_ICONS[row.tab] || LayoutGrid;
                const active = dashTabResolved != null && dashTabResolved === row.tab;
                return (
                  <SidebarNavTooltip key={row.tab} collapsed={sidebarCollapsed} label={row.label}>
                    <Link
                      to={dashHref(row.tab)}
                      data-testid={row.testId || `member-dash-${row.tab}`}
                      onClick={() => {
                        setMobileNavOpen(false);
                        onDashboardNavClick?.(row.tab);
                      }}
                      className={cn(
                        "relative flex w-full items-center rounded-lg text-left text-sm font-medium transition-colors",
                        sidebarCollapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5",
                        active
                          ? "bg-white/10 text-white ring-1 ring-white/10"
                          : "text-slate-400 hover:bg-white/5 hover:text-white",
                      )}
                    >
                      <Icon className={cn("h-[18px] w-[18px] shrink-0", active ? "text-white" : "text-slate-500")} aria-hidden />
                      {!sidebarCollapsed ? <span className="min-w-0 flex-1 truncate">{row.label}</span> : null}
                      {row.badge ? (
                        <span
                          className={cn(
                            "rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.7)]",
                            sidebarCollapsed
                              ? "absolute -right-0.5 -top-0.5 h-2 w-2"
                              : "h-2 w-2 shrink-0",
                          )}
                          aria-hidden
                        />
                      ) : null}
                    </Link>
                  </SidebarNavTooltip>
                );
              })}
            </div>
            <div className="mx-3 my-3 border-t border-white/10" />
            {!sidebarCollapsed ? (
            <p className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Découvrir &amp; compte
            </p>
          ) : null}
          </div>
        ) : null}
        {globalNavItems.map(({ key, to, label, Icon, active }) => (
          <SidebarNavTooltip key={key} collapsed={sidebarCollapsed} label={label}>
            <Link
              to={to}
              data-testid={`member-nav-${key}`}
              onClick={() => setMobileNavOpen(false)}
              className={cn(
                "flex w-full items-center rounded-lg text-left text-sm font-medium transition-colors",
                sidebarCollapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5",
                active ? "bg-white/10 text-white ring-1 ring-white/10" : "text-slate-400 hover:bg-white/5 hover:text-white",
              )}
            >
              <Icon className={cn("h-[18px] w-[18px] shrink-0", active ? "text-white" : "text-slate-500")} aria-hidden />
              {!sidebarCollapsed ? <span className="min-w-0 flex-1 truncate">{label}</span> : null}
            </Link>
          </SidebarNavTooltip>
        ))}
      </nav>

      <div className={cn("shrink-0 space-y-0.5 border-t border-white/10", sidebarCollapsed ? "p-2" : "p-3")}>
        {user?.role === "admin" ? (
          <SidebarNavTooltip collapsed={sidebarCollapsed} label="Administration">
            <Link
              to="/admin"
              onClick={() => setMobileNavOpen(false)}
              className={cn(
                "flex items-center rounded-lg text-sm font-medium transition-colors",
                sidebarCollapsed ? "justify-center px-2 py-2.5" : "gap-2 px-3 py-2",
                pathname.startsWith("/admin")
                  ? "bg-white/10 text-white"
                  : "text-slate-300 hover:bg-white/5 hover:text-white",
              )}
              data-testid="member-sidebar-admin"
            >
              <MEMBER_GLOBAL_NAV_ICONS.admin className="h-4 w-4 shrink-0" aria-hidden />
              {!sidebarCollapsed ? <span>Administration</span> : null}
            </Link>
          </SidebarNavTooltip>
        ) : null}
        <SidebarNavTooltip collapsed={sidebarCollapsed} label="Déconnexion">
          <button
            type="button"
            onClick={() => {
              void (async () => {
                await logout();
                navigate("/");
              })();
            }}
            className={cn(
              "flex w-full items-center rounded-lg text-left text-sm font-medium text-slate-400 transition-colors hover:bg-red-950/40 hover:text-red-200",
              sidebarCollapsed ? "justify-center px-2 py-2.5" : "gap-2 px-3 py-2",
            )}
            data-testid="member-sidebar-logout"
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden />
            {!sidebarCollapsed ? <span>Déconnexion</span> : null}
          </button>
        </SidebarNavTooltip>
      </div>
    </div>
  );

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  const showTitles = Boolean((title || "").trim() || (subtitle || "").trim());
  const initial = user?.name?.trim?.()?.[0]?.toUpperCase?.() || "U";

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex min-h-screen bg-[#f1f5f9]">
        <div
          className="fixed left-0 top-0 z-30 hidden h-[100dvh] min-h-[100dvh] lg:block"
          style={{ width: sidebarWidth }}
        >
          <aside className="h-full overflow-hidden border-r border-slate-800/80 bg-[#0f172a] transition-[width] duration-200 ease-out">
            <SidebarBody sidebarCollapsed={collapsed} />
          </aside>
          <SidebarCollapseTab
            collapsed={collapsed}
            onToggle={toggleCollapsed}
            testId="member-sidebar-toggle"
          />
        </div>

        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className="w-[min(100vw-2rem,280px)] border-slate-800 bg-[#0f172a] p-0 text-white">
            <SheetHeader className="sr-only">
              <SheetTitle>Menu Mon espace</SheetTitle>
            </SheetHeader>
            <SidebarBody navScrollable />
          </SheetContent>
        </Sheet>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col transition-[padding] duration-200 ease-out lg:pl-[var(--sidebar-w)]" style={{ "--sidebar-w": `${sidebarWidth}px` }}>
          <header className="sticky top-0 z-20 border-b border-slate-200/90 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0 border-slate-200 lg:hidden"
                  onClick={() => setMobileNavOpen(true)}
                  aria-label="Ouvrir le menu"
                >
                  <AlignJustify className="h-5 w-5" />
                </Button>
                {showTitles ? (
                  <div className="min-w-0">
                    <h1 className="font-display text-xl font-bold tracking-tight text-[#0B0D12] sm:text-2xl">
                      {(title || "").trim()}
                    </h1>
                    {typeof subtitle === "string" && subtitle.trim() ? (
                      <p className="mt-0.5 text-sm text-slate-600">{subtitle.trim()}</p>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="flex shrink-0 items-center justify-end gap-4 sm:gap-5">
                {showWriteUs ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    onClick={() => setWriteUsOpen(true)}
                    data-testid="member-write-us"
                  >
                    <Mail className="h-4 w-4" aria-hidden />
                    <span className="hidden sm:inline">Nous écrire</span>
                  </Button>
                ) : null}
                <MemberHeaderWallet />
                <div className="ml-0.5 hidden h-7 w-px shrink-0 bg-slate-200/90 sm:block" aria-hidden />

                <div
                  className="flex max-w-[min(100%,14rem)] min-w-0 items-center gap-2 py-1 pl-0.5 pr-1.5 sm:max-w-[16rem] sm:gap-2.5 sm:py-1.5 sm:pl-1 sm:pr-2"
                  data-testid="member-user-chip"
                  aria-label="Compte connecté"
                >
                  <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#2D7FF9] to-[#1a5fd4] text-xs font-bold text-white sm:h-9 sm:w-9"
                    aria-hidden
                  >
                    {initial}
                  </div>
                  <div className="min-w-0 flex-1 hidden sm:block">
                    <div className="truncate text-sm font-medium leading-tight text-[#0B0D12]">
                      {user?.name?.trim() ? user.name.trim() : "Compte"}
                    </div>
                    <div className="truncate text-[11px] leading-tight text-slate-500">
                      {(user?.email && String(user.email).trim()) || "—"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <main
            className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6"
            data-testid="member-main-scroll"
          >
            {children}
          </main>
        </div>
      </div>

      <Dialog open={writeUsOpen} onOpenChange={setWriteUsOpen}>
        <DialogContent
          className="max-h-[min(90vh,720px)] w-[calc(100vw-1.5rem)] max-w-lg overflow-y-auto sm:rounded-2xl"
          data-testid="member-write-us-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Nous écrire</DialogTitle>
            <DialogDescription className="text-left text-sm text-slate-600">
              Question simple, partenariat, idée de produit : choisissez le type d’échange, nous lisons tout.
            </DialogDescription>
          </DialogHeader>
          <ProductSuggestionForm context="member-shell" onSubmitted={() => setWriteUsOpen(false)} />
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
